import { forwardRef, useContext } from 'react';
import styles from '../Input.module.css';
import { InputContext } from '../Input';
import { altoConsole } from '../../../../utils/console/console';

/**
 * Text-only prefix element rendered at the start of the input.
 * Examples: currency symbol "$", country code "+1", unit symbol "kg"
 *
 * Restrictions:
 * - Only accepts string children (no components)
 * - Does not support interactive elements (use InputStartSlot for buttons/icons)
 * - Automatically disabled when parent Input is disabled
 * - Can be hidden visually but visible to screen readers via visuallyHiddenLabel
 */
interface PrefixProps {
  /** The prefix text to display (e.g., "$", "+1", "kg") */
  children: string;
  /** Optional accessible label for screen readers when prefix is visual-only */
  visuallyHiddenLabel?: string;
}

/**
 * Text-only prefix component for Input.
 * Renders as a styled div with accessibility attributes from InputContext.
 * Responds to disabled/invalid state automatically from parent Input.
 */
const InputPrefix = forwardRef<HTMLDivElement, PrefixProps>(
  ({ children, visuallyHiddenLabel }, ref) => {
    // Read disabled/invalid state from InputContext
    const state = useContext(InputContext);

    // Validate that children is a string (not React components)
    if (typeof children !== 'string') {
      altoConsole.error(
        'Input.Prefix only accepts a single string as a child',
        { scope: 'Input' },
      );
    }

    return (
      <div
        ref={ref}
        className={styles['input-field__prefix']}
        // Propagate disabled/invalid state from InputContext to DOM for CSS selectors
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
        data-prefix-type="text"
        // Hide from screen readers only if we have an accessible label
        aria-hidden={visuallyHiddenLabel ? 'true' : undefined}
      >
        {children}
      </div>
    );
  },
);
InputPrefix.displayName = 'Input.Prefix';

export default InputPrefix;
